import { goalsFr } from "@domains/goals/client/i18n/fr.ts";
import { goodVibesFr, goodVibeCardFr, goodVibeDetailDialogFr } from "@domains/good-vibes/client/i18n/fr.ts";
import type { en } from "./en.ts";

export const fr: typeof en = {
    ...goalsFr,
    ...goodVibesFr,
    Errors: {
        retry: "Réessayer",
        retrying: "Nouvelle tentative",
        reassurance: "Veuillez réessayer plus tard.",
        refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
    },
    Navigation: {
        employees: { label: "Employés" },
        organization: {
            label: "Org",
            description: "Vue d'ensemble, employés, Bonnes Vibes",
            main: {
                label: "Org",
                overview: { label: "Vue d'ensemble" },
                employees: { label: "Personnes" },
                "good-vibes": { label: "Bonnes Vibes" },
                goals: { label: "Objectifs" },
                feedback: { label: "Feedback" },
                "org-chart": { label: "Organigramme" }
            }
        },
        "organization-goal": { label: "Objectif" },
        "organization-goal-edit": { label: "Modifier l'objectif" },
        "employee-meeting": { label: "Réunion" },
        "employee-review": { label: "Évaluation" },
        "employee-progression-reviews": { label: "Évaluations", description: "Vos évaluations sous forme de tableau" },
        "employee-review-write": { label: "Rédiger l'évaluation" },
        "employee-review-share": { label: "Partager l'évaluation" },
        "employee-goal": { label: "Objectif" },
        "employee-goal-edit": { label: "Modifier l'objectif" },
        "employee-progression-goals": { label: "Objectifs", description: "Vos objectifs sous forme de tableau" },
        "employee-engagement-feedback": { label: "Votre feedback", description: "Tout le feedback que vous avez envoyé" },
        "employee-profile": {
            label: "Profil",
            main: {
                label: "Profil",
                overview: { label: "Vue d'ensemble" },
                goals: { label: "Objectifs" },
                reviews: { label: "Évaluations" },
                timeline: { label: "Chronologie" },
                "good-vibes": { label: "Bonnes Vibes" },
                meetings: { label: "Réunions" },
                engagement: { label: "Engagement" },
                progression: { label: "Progression" }
            }
        }
    },
    PeopleListPage: {
        error: "Nous n'avons pas pu charger l'annuaire pour le moment.",
        noResults: "Aucun employé trouvé",
        noData: "Aucun employé pour le moment.",
        noResultsHint: "Essayez d'ajuster les filtres.",
        totalCount_one: "{{count}} employé",
        totalCount_other: "{{count}} employés",
        viewToggle: {
            groupLabel: "Mode d'affichage",
            tableView: "Vue liste",
            cardView: "Vue cartes"
        },
        liveRegion: {
            loading: "Chargement des employés",
            sortedAscending: "Tableau trié par {{column}}, ordre croissant",
            sortedDescending: "Tableau trié par {{column}}, ordre décroissant",
            loadingMoreAnnouncement: "Chargement d'employés supplémentaires",
            loadedMore_one: "{{count}} employé supplémentaire chargé",
            loadedMore_other: "{{count}} employés supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        }
    },
    DirectorySettingsMenu: {
        trigger: "Paramètres de l'annuaire",
        managePeople: "Gérer les employés",
        customizeFields: "Personnaliser les champs de l'annuaire"
    },
    PeopleListToolbar: {
        searchPlaceholder: "Rechercher des employés",
        searchAriaLabel: "Rechercher des employés",
        resetFilters: "Effacer les filtres",
        filters: {
            department: "Département",
            location: "Emplacement",
            departmentAriaLabel: "Filtrer par département",
            locationAriaLabel: "Filtrer par emplacement",
            clearFilter: "Effacer le filtre"
        }
    },
    PeopleTableView: {
        ariaLabel: "Tableau des employés",
        columns: {
            name: "Nom",
            title: "Titre",
            department: "Département",
            location: "Emplacement"
        }
    },
    PeopleCardsView: {
        ariaLabel: "Employés"
    },
    PeopleYouBadge: {
        you: "Vous"
    },
    ProfileMeetingsPage: {
        error: "Nous n'avons pas pu charger vos réunions pour le moment.",
        noData: "Aucune réunion pour le moment. Elles apparaîtront ici dès qu'elles seront à votre agenda.",
        noDataForPerson: "Aucune réunion avec cette personne pour le moment. Elles apparaîtront ici dès qu'elles seront à votre agenda.",
        noResults: "Aucune réunion trouvée",
        noResultsHint: "Essayez un autre terme de recherche ou effacez vos filtres.",
        clearFilters: "Effacer les filtres",
        totalCount_one: "{{count}} réunion",
        totalCount_other: "{{count}} réunions",
        liveRegion: {
            sortChanged: "Trié par date {{direction}}",
            ascending: "ordre croissant",
            descending: "ordre décroissant",
            loading: "Chargement des réunions",
            loadingMoreAnnouncement: "Chargement d'autres réunions",
            loadedMore_one: "{{count}} réunion supplémentaire chargée",
            loadedMore_other: "{{count}} réunions supplémentaires chargées",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        }
    },
    MeetingDetailPage: {
        breadcrumb: {
            people: "Employés"
        }
    },
    ProfileTabsHeading: {
        liveRegion: {
            loading: "Chargement du profil"
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            people: "Employés"
        }
    },
    ProfileOverviewPage: {
        empty: "Il n'y a rien à afficher sur ce profil pour l'instant.",
        loadError: "Ce profil n'a pas pu être chargé pour le moment. Actualisez pour réessayer.",
        liveRegion: {
            loading: "Chargement du profil"
        }
    },
    ProfileHeader: {
        newHireBadge: "Nouvel employé",
        reportsTo: "Relève de",
        joined: "Arrivé en {{date}}"
    },
    ProfileGoalsPage: {
        create: {
            newGoal: "Nouvel objectif",
            error: "Erreur lors de la création de l’objectif"
        },
        liveRegion: {
            loading: "Chargement des objectifs",
            sortChanged: "Objectifs triés par {{option}}"
        },
        empty: {
            headline: "Aucun objectif pour {{firstName}} ce trimestre.",
            subtext: "Les objectifs définis ici apparaissent dans les revues et les ordres du jour des rencontres individuelles.",
            noMatch: {
                headline: "Aucun objectif ne correspond à ces filtres pour {{firstName}}.",
                subtext: "Essayez d'ajuster ou d'effacer vos filtres."
            }
        },
        error: "Nous n'avons pas pu charger les objectifs pour le moment."
    },
    ProfileGoalsToolbar: {
        searchPlaceholder: "Rechercher des objectifs",
        searchAriaLabel: "Rechercher des objectifs",
        relationship: {
            ariaLabel: "Filtrer par relation",
            clearAriaLabel: "Effacer le filtre de relation",
            owner: "Responsable",
            contributor: "Contributeur",
            all: "Responsable ou contributeur"
        },
        status: {
            ariaLabel: "Filtrer par statut",
            clearAriaLabel: "Effacer le filtre de statut",
            all: "Tous les statuts",
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        publicationState: {
            label: "État",
            ariaLabel: "Filtrer par état",
            trigger: "État : {{states}}",
            separator: ", ",
            active: "Actif",
            draft: "Brouillon",
            archived: "Archivé",
            reset: "Rétablir par défaut"
        },
        goalType: {
            ariaLabel: "Filtrer par type",
            clearAriaLabel: "Effacer le filtre de type",
            all: "Tous les types",
            organization: "Organisation",
            team: "Équipe",
            individual: "Individuel"
        },
        resetFilters: "Effacer les filtres",
        sort: {
            ariaLabel: "Trier les objectifs par",
            label: "Trier",
            options: {
                lastUpdatedAt: "Récemment mis à jour",
                progress: "Progression",
                dueDate: "Date d'échéance",
                title: "Titre"
            }
        },
        goalCount_one: "{{count}} objectif",
        goalCount_other: "{{count}} objectifs"
    },
    GoalActions: {
        ariaLabel: "Actions pour {{title}}",
        edit: "Modifier l’objectif",
        updateProgress: "Mettre à jour le progrès",
        duplicate: "Dupliquer l’objectif",
        archive: "Archiver l’objectif",
        unarchive: "Désarchiver l’objectif",
        delete: "Supprimer l’objectif",
        cancel: "Annuler",
        untitled: "Objectif sans titre",
        archiveDialog: {
            title: "Archiver l’objectif",
            description: "{{title}} sortira des objectifs actifs. Vous pourrez le désarchiver à tout moment."
        },
        unarchiveDialog: {
            title: "Désarchiver l’objectif",
            description: "{{title}} reviendra dans les objectifs actifs."
        },
        deleteDialog: {
            title: "Supprimer l’objectif?",
            description: "{{title}} sera supprimé. Cette action est irréversible."
        },
        toast: {
            duplicated: "Objectif dupliqué",
            duplicateError: "Erreur lors de la duplication de l’objectif",
            archived: "Objectif archivé",
            archiveError: "Erreur lors de l’archivage de l’objectif",
            unarchived: "Objectif désarchivé",
            unarchiveError: "Erreur lors du désarchivage de l’objectif",
            deleted: "Objectif supprimé",
            deleteError: "Erreur lors de la suppression de l’objectif"
        }
    },
    GoalCard: {
        deletedMember: "Membre supprimé",
        untitled: "Objectif sans titre",
        draft: "Brouillon",
        archived: "Archivé",
        type: {
            personal: "Objectif individuel",
            team: "Objectif d'équipe",
            organization: "Objectif d'organisation"
        },
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        alignedTo: "Aligné sur",
        progressAriaLabel: "Progression de l'objectif",
        progressPercent: "{{value}} %",
        dueDate: "Échéance {{date}}",
        overdue: "En retard",
        latestUpdateLabel: "Dernière mise à jour",
        updateMeta: "{{author}} · {{time}}",
        noUpdates: "Aucune mise à jour publiée pour l'instant.",
        relativeTime: {
            today: "Aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours",
            weeks_one: "il y a {{count}} semaine",
            weeks_other: "il y a {{count}} semaines"
        }
    },
    EngagementFeedbackListPage: {
        pageTitle: "Votre feedback",
        breadcrumb: {
            ariaLabel: "Fil d'Ariane Votre feedback",
            here: "Votre feedback"
        }
    },
    ProfileReviewsListPage: {
        title: "Vos évaluations",
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            reviews: "Évaluations"
        },
        viewAs: {
            ariaLabel: "Afficher les évaluations en tant que",
            clearAriaLabel: "Effacer le filtre de portée",
            reviewee: "En tant qu'évalué",
            reviewer: "En tant qu'évaluateur"
        },
        error: "Nous n'avons pas pu charger les évaluations pour le moment.",
        accessDenied: {
            headline: "Vous n'avez pas accès à ces évaluations",
            subtext: "Ces évaluations sont confidentielles entre l'évalué et ses évaluateurs."
        },
        empty: {
            heading: "Aucune évaluation pour l'instant",
            hint: "Les évaluations partagées avec vous apparaîtront ici dès qu'un cycle d'évaluation sera en cours."
        },
        noResults: "Aucune évaluation ne correspond à vos filtres",
        noResultsHint: "Essayez d'ajuster les filtres ou la recherche.",
        totalCount_one: "{{count}} évaluation",
        totalCount_other: "{{count}} évaluations",
        liveRegion: {
            loading: "Chargement des évaluations",
            loadingMoreAnnouncement: "Chargement d'autres évaluations",
            loadedMore_one: "{{count}} évaluation supplémentaire chargée",
            loadedMore_other: "{{count}} évaluations supplémentaires chargées",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        }
    },
    ProgressionGoalsListPage: {
        pageTitle: "Vos objectifs",
        liveRegion: {
            loading: "Chargement des objectifs",
            sortChanged: "Trié par {{column}}"
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane des objectifs",
            here: "Objectifs"
        },
        empty: {
            headline: "Vous n'avez encore aucun objectif.",
            subtext: "Les objectifs définis ici apparaissent dans les revues et les ordres du jour des rencontres individuelles.",
            noMatch: {
                headline: "Aucun objectif ne correspond à ces filtres.",
                subtext: "Essayez d'ajuster ou d'effacer vos filtres."
            }
        },
        totalCount_one: "{{count}} objectif",
        totalCount_other: "{{count}} objectifs",
        error: "Nous n'avons pas pu charger les objectifs pour le moment."
    },
    ProgressionGoalsTable: {
        ariaLabel: "Mes objectifs",
        untitledGoal: "Objectif sans titre",
        columns: {
            goal: "Objectif",
            due: "Échéance",
            status: "Statut",
            progress: "Progression",
            updated: "Mise à jour",
            actions: "Actions"
        }
    },
    ProgressionGoalRow: {
        deletedMember: "Membre supprimé",
        draft: "Brouillon",
        archived: "Archivé",
        untitled: "Objectif sans titre",
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        type: {
            organization: "Organisation",
            team: "Équipe",
            personal: "Individuel"
        },
        noDate: "Aucune date",
        overdue: "En retard",
        progressAriaLabel: "Progression de l'objectif",
        progressPercent: "{{value}} %",
        updated: {
            never: "Jamais",
            today: "Aujourd'hui",
            days: "il y a {{count}} j",
            weeks: "il y a {{count}} sem"
        }
    },
    ProfileReviewsPage: {
        error: "Nous n'avons pas pu charger les évaluations pour le moment.",
        accessDenied: {
            headline: "Vous n'avez pas accès à ces évaluations",
            subtext: "Ces évaluations sont confidentielles entre l'évalué et ses évaluateurs."
        },
        empty: {
            heading: "Aucune évaluation pour l'instant",
            hint: "Les évaluations de rendement de cette personne apparaîtront ici dès qu'un cycle d'évaluation sera en cours."
        },
        noResults: "Aucune évaluation trouvée",
        noResultsHint: "Essayez d'ajuster les filtres.",
        totalCount_one: "{{count}} évaluation",
        totalCount_other: "{{count}} évaluations",
        liveRegion: {
            loading: "Chargement des évaluations",
            loadingMoreAnnouncement: "Chargement d'autres évaluations",
            loadedMore_one: "{{count}} évaluation supplémentaire chargée",
            loadedMore_other: "{{count}} évaluations supplémentaires chargées",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        }
    },
    ReviewsToolbar: {
        searchPlaceholder: "Rechercher des évaluations",
        searchAriaLabel: "Rechercher des évaluations",
        scope: {
            ariaLabel: "Période",
            latest: "Dernière évaluation",
            past90: "90 derniers jours",
            pastYear: "Dernière année",
            allTime: "Depuis toujours"
        },
        cycleStatus: {
            ariaLabel: "Statut du cycle",
            all: "De tous les cycles",
            active: "Des cycles actifs",
            locked: "Des cycles verrouillés"
        },
        sharing: {
            ariaLabel: "Partage",
            all: "Tous les partages",
            notShared: "Non partagée",
            shared: "Partagée",
            acknowledged: "Pris connaissance"
        },
        clearScope: "Effacer le filtre de période",
        clearCycleStatus: "Effacer le filtre de cycle",
        clearSharing: "Effacer le filtre de partage",
        resetFilters: "Effacer les filtres"
    },
    ReviewsTable: {
        ariaLabel: "Évaluations",
        columns: {
            review: "Évaluation",
            score: "Note",
            period: "Période",
            types: "Types",
            sharing: "Partage"
        },
        expandRow: "Afficher les évaluateurs pour {{reviewee}}",
        collapseRow: "Masquer les évaluateurs pour {{reviewee}}",
        drawerRow: "Évaluateurs pour {{reviewee}}",
        noReviewsGroup: "Aucune évaluation ({{count}})"
    },
    ReviewRow: {
        noReviewsYet: "Aucune évaluation pour l'instant",
        mobileLabels: {
            score: "Note d'évaluation",
            period: "Période d'évaluation",
            types: "Types d'évaluation",
            sharing: "Partage"
        },
        types: {
            self: "Auto",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        sharing: {
            notShared: "Non partagée",
            shared: "Partagée",
            acknowledged: "Pris connaissance"
        }
    },
    ReviewLotDrawer: {
        liveRegion: {
            loading: "Chargement des évaluateurs"
        },
        error: "Impossible de charger les évaluateurs.",
        retry: "Réessayer",
        empty: "Aucun évaluateur pour cette évaluation."
    },
    LotReviewerRow: {
        roles: {
            self: "Auto",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        status: {
            notStarted: "Non commencée",
            inProgress: "En cours",
            submitted: "Soumise",
            overdue: "En retard"
        },
        submittedOn: "le {{date}}",
        dueOn: "Échéance {{date}}",
        anonymous: {
            peer: "Pair anonyme",
            upward: "Évaluateur ascendant anonyme",
            reviewer: "Évaluateur anonyme"
        },
        noDirectManager: "Pas de gestionnaire immédiat",
        deletedMember: "Membre supprimé"
    },
    ReviewWritePage: {
        untitledCycle: "Évaluation",
        breadcrumb: { ariaLabel: "Fil d’Ariane", people: "Employés", progression: "Progression", reviews: "Évaluations", reviewType: { manager: "Évaluation du gestionnaire", self: "Auto-évaluation", peer: "Évaluation par les pairs", upward: "Évaluation ascendante" } },
        cycleDescription: { expand: "Afficher plus", collapse: "Afficher moins" },
        perspectives: { yours: "Votre évaluation", manager: "Gestionnaire", self: "Auto-évaluation", peer: "Pairs", upward: "Ascendante" },
        assistant: {
            welcomeMessage: "Bonjour {{reviewerName}}, commençons l'évaluation de {{revieweeName}}",
            welcomeMessageSelf: "Bonjour {{reviewerName}}, commençons votre évaluation",
            subtext: {
                role: "actuellement {{jobTitle}}",
                team: "{{name}} fait partie de l'équipe {{department}}",
                teamTenure_one: "{{name}} est chez {{company}} depuis {{count}} an, dans l'équipe {{department}}",
                teamTenure_other: "{{name}} est chez {{company}} depuis {{count}} ans, dans l'équipe {{department}}",
                teamTenureLessThanYear: "{{name}} est chez {{company}} depuis moins d'un an, dans l'équipe {{department}}",
                tenure_one: "{{name}} est chez {{company}} depuis {{count}} an",
                tenure_other: "{{name}} est chez {{company}} depuis {{count}} ans",
                tenureLessThanYear: "{{name}} est chez {{company}} depuis moins d'un an",
                selfTenure_one: "{{count}} an chez {{company}}",
                selfTenure_other: "{{count}} ans chez {{company}}",
                selfTenureLessThanYear: "Moins d'un an chez {{company}}"
            },
            inputPlaceholder: "Demandez-moi de préparer ou de rédiger une réponse…",
            initialSuggestion: "Aide-moi à préparer l'évaluation"
        },
        loading: "Chargement de l'évaluation",
        progress: "{{answered}} réponse(s) sur {{total}}",
        autoSave: {
            saving: "Enregistrement…",
            saved: "Enregistré",
            error: "Échec de l'enregistrement — nouvelle tentative"
        },
        submit: "Soumettre l'évaluation",
        submitBlocked: "Répondez à toutes les questions obligatoires pour soumettre",
        submitSucceeded: "Évaluation soumise",
        submitFailed: "Impossible de soumettre l'évaluation",
        submitBlockedUnsaved: "Votre dernière réponse n'a pas pu être enregistrée, l'évaluation n'a donc pas été soumise. Nous réessayons — retentez dans un instant.",
        cancel: "Annuler",
        update: "Mettre à jour l'évaluation",
        updateSucceeded: "Évaluation mise à jour",
        unsavedChanges: "Vous avez des modifications non enregistrées",
        saveFailed: "Vos modifications n'ont pas pu être enregistrées. Elles sont toujours là — réessayez.",
        assistantUpdated: "Workleap AI a mis à jour cette évaluation",
        discard: {
            heading: "Quitter sans enregistrer?",
            body: "Vos modifications à cette évaluation n'ont pas été enregistrées. Quitter maintenant les supprimera.",
            keepEditing: "Continuer l'édition",
            discard: "Supprimer les modifications"
        },
        showAnswersInline: "Afficher les réponses en contexte",
        question: {
            optional: "Facultative",
            textPlaceholder: "Rédigez votre réponse…",
            ratingLabel: "Note",
            choiceLabel: "Choix",
            unlabeledValue: "Niveau {{value}}",
            inlineSelectedBy: "Aussi sélectionné par {{names}}",
            notInYourReview: "Ne fait pas partie de votre évaluation"
        },
        context: {
            title: "Contexte",
            open: "Afficher le contexte",
            close: "Masquer le contexte",
            workleapGroup: "Workleap",
            externalGroup: "Externe",
            emptyGroup: "Rien pour l'instant",
            progress: "{{value}} %",
            showing: "{{shown}} élément(s) sur {{total}}",
            viewAll: "Tout afficher ({{count}})",
            back: "Retour",
            sourcesCount: "{{count}} sources",
            sourceFrom: "provenant de {{source}}",
            sourceTypes: {
                Role: "Rôle",
                Goals: "Objectifs",
                CurrentReviews: "Évaluations en cours",
                PastReviews: "Évaluations précédentes",
                Recognition: "Good Vibes",
                Agendas: "1on1s",
                Engagement: "Engagement",
                Feedback: "Rétroaction"
            },
            visibility: {
                public: "Publique",
                direct: "Directe"
            }
        }
    },
    ReviewDetailsPage: {
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            people: "Employés",
            progression: "Progression",
            reviews: "Évaluations"
        },
        liveRegion: {
            loading: "Chargement de l'évaluation"
        },
        error: "Nous n'avons pas pu charger cette évaluation pour le moment.",
        accessDenied: {
            headline: "Vous n'avez pas accès à cette évaluation",
            subtext: "Cette évaluation est confidentielle entre l'évalué et ses évaluateurs."
        },
        empty: {
            heading: "Cette évaluation n'a pas encore commencé",
            hint: "Les réponses apparaîtront ici dès que l'évaluation sera en cours."
        }
    },
    ReviewDetailHeader: {
        untitledCycle: "Évaluation de performance",
        covers: "Couvre {{period}}",
        visibility: {
            reportingLine: "Visible par le gestionnaire immédiat de {{name}} et ses supérieurs"
        },
        acknowledgedBy: "Prise de connaissance par {{name}} · {{date}}",
        sharedBy: "Partagée par {{name}} · {{date}}"
    },
    ReviewSharePreview: {
        pageTitle: "Aperçu de l'évaluation partagée",
        visibilityListHeader: "Voici la vue qui sera partagée avec {{name}}. Cela inclut :",
        types: {
            manager: "Les réponses du gestionnaire auxquelles il a accès",
            self: "Les réponses de son auto-évaluation",
            peerAnonymous: "Les réponses des pairs auxquelles il a accès (anonymisées)",
            peerWithNames: "Les réponses des pairs auxquelles il a accès (avec les noms visibles)",
            upwardAnonymous: "Les réponses des évaluations ascendantes auxquelles il a accès (anonymisées)",
            upwardWithNames: "Les réponses des évaluations ascendantes auxquelles il a accès (avec les noms visibles)"
        },
        editsVisible: "Toute modification effectuée après le partage sera également visible.",
        acknowledgmentWarning: "Ce cycle exige une prise de connaissance, vous ne pourrez donc plus retirer le partage une fois celui-ci effectué.",
        cancel: "Annuler",
        share: "Partager"
    },
    ReviewDetailActions: {
        actions: "Actions de l'évaluation",
        preparing: "Préparation…",
        manage: "Gérer l'évaluation"
    },
    ReviewExportAction: {
        exportPdf: "Exporter en PDF",
        preparing: "Préparation…",
        fullVersion: "Version complète",
        fullVersionDescription: "Toutes les questions et réponses de cette évaluation.",
        revieweeVersion: "Version de la personne évaluée",
        revieweeVersionDescription: "Uniquement ce que la personne évaluée peut voir. Les questions qui lui sont masquées sont exclues.",
        successToast: "Le fichier PDF a bien été exporté.",
        errorToast: "L'export a échoué. Veuillez réessayer."
    },
    ReviewReleaseActions: {
        previewAndShare: "Prévisualiser et partager",
        editReview: "Modifier l'évaluation",
        reviewType: {
            manager: "Évaluation du gestionnaire",
            self: "Auto-évaluation",
            peer: "Évaluation par les pairs",
            upward: "Évaluation ascendante"
        },
        shareDisabledReason: "Cette évaluation ne peut pas être partagée car toutes les questions sont masquées pour la personne évaluée.",
        unshare: "Retirer le partage",
        acknowledge: "Prendre connaissance",
        cancel: "Annuler",
        unshareDialog: {
            title: "Retirer le partage de cette évaluation?",
            description: "{{name}} perdra l'accès aux commentaires du gestionnaire et des pairs pour cette évaluation jusqu'à ce qu'elle soit partagée à nouveau.",
            confirm: "Retirer le partage"
        },
        acknowledgeDialog: {
            title: "Prendre connaissance de cette évaluation?",
            description: "Confirmer que vous avez pris connaissance de cette évaluation. Votre nom et la date seront enregistrés sur l'évaluation comme preuve de réception.",
            confirmation: "Je confirme avoir lu cette évaluation",
            confirm: "Prendre connaissance"
        },
        shareSuccessToast: "Vous avez partagé cette évaluation",
        unshareSuccessToast: "Vous avez retiré le partage de cette évaluation",
        acknowledgeSuccessToast: "Vous avez pris connaissance de cette évaluation",
        reviewChangedToast: "Cette évaluation a changé depuis que vous l'avez ouverte. Nous l'avons actualisée : veuillez prendre connaissance de l'évaluation à nouveau.",
        errorToast: "Désolé, une erreur s'est produite. Veuillez réessayer."
    },
    ParticipantCard: {
        kind: {
            reviewee: "Personne évaluée",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        anonymousName: {
            peer: "Pair anonyme",
            upward: "Évaluateur ascendant anonyme"
        },
        managerRole: {
            direct: "Gestionnaire immédiat",
            temporary: "gestionnaire immédiat temporaire",
            previous: "gestionnaire immédiat précédent"
        },
        status: {
            notStarted: "Non commencée",
            inProgress: "En cours",
            submitted: "Soumise"
        },
        submittedOn: "le {{date}}",
        dueOn: "Échéance {{date}}",
        deletedMember: "Membre supprimé",
        noDirectManager: "Pas de gestionnaire immédiat"
    },
    ReviewQuestionCard: {
        badges: {
            noAnswer: "Aucune réponse",
            optional: "Facultative"
        },
        emptyAnswer: {
            text: "Aucune réponse pour l'instant.",
            rating: "Aucune note pour l'instant.",
            choice: "Aucune sélection pour l'instant."
        },
        reviewerKind: {
            manager: "Évaluation du gestionnaire",
            self: "Auto-évaluation",
            peer: "Évaluation du pair",
            upward: "Évaluation ascendante"
        },
        anonymousReviewer: "Évaluateur anonyme",
        deletedMember: "Membre supprimé",
        ratingUnlabeledValue: "Niveau {{value}}",
        offScaleAnswers: "Réponses hors de l'échelle actuelle",
        selectedBy: "Choisi par {{names}}"
    },
    ReviewSummary: {
        eyebrow: "Résumé IA",
        disclaimer: "Ce résumé a été généré par l'IA et peut contenir des inexactitudes. Consultez-le avec les réponses complètes ci-dessous.",
        errorToast: "Une erreur est survenue. Veuillez réessayer.",
        callout: {
            heading: "Besoin d'un aperçu rapide?",
            body: "Générez un résumé IA pour voir les principaux thèmes de toutes les évaluations soumises, au même endroit.",
            generate: "Générer un résumé",
            generating: "Génération du résumé…",
            noTextQuestions: "Un résumé ne peut pas être généré, car ce cycle d'évaluation ne contient aucune question de type texte."
        },
        menu: {
            triggerAriaLabel: "Actions du résumé",
            regenerate: "Régénérer",
            dismiss: "Fermer"
        },
        liveRegion: {
            loading: "Chargement du résumé de l'évaluation…"
        }
    },
    GoalDetailPage: {
        editGoal: "Modifier l’objectif",
        draft: "Brouillon",
        archived: "Archivé",
        liveRegion: {
            loading: "Chargement de l'objectif"
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            people: "Employés",
            progression: "Progression",
            goals: "Objectifs"
        },
        type: {
            organization: "Objectif d'organisation",
            team: "Objectif d'équipe",
            personal: "Objectif individuel"
        },
        sidebar: {
            owner: "Responsable",
            deletedMember: "Membre supprimé",
            unknownTeam: "Équipe inconnue",
            entireOrganization: "Toute l’organisation",
            teamKind: "Équipe",
            directReportsOf: "Subordonnés directs de {{name}}",
            reportingLineOf: "Ligne hiérarchique de {{name}}",
            createdBy: "Créé par {{name}}",
            contributors: "Contributeurs ({{count}})",
            showAllContributors_one: "Afficher tous les contributeurs ({{count}})",
            showAllContributors_other: "Afficher tous les contributeurs ({{count}})",
            showLessContributors: "Afficher moins",
            parentGoal: "Objectif parent",
            startDate: "Date de début",
            due: "Échéance",
            visibility: "Visibilité"
        },
        parentScope: {
            organization: "Organisation",
            team: "Équipe",
            personal: "Individuel"
        },
        due: {
            dueIn_one: "dans {{count}} jour",
            dueIn_other: "dans {{count}} jours",
            today: "Échéance aujourd'hui",
            overdue: "En retard"
        },
        visibility: {
            allOrganization: "Visible par l'organisation",
            ownerAndContributorsOnly: "Visible par le responsable, les contributeurs et leurs supérieurs hiérarchiques"
        },
        description: {
            heading: "Description"
        },
        progress: {
            heading: "Progression",
            percent: "{{value}} %",
            ariaLabel: "Progression de {{title}}"
        },
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        trackingMethod: {
            simple: "Progression simple — un seul pourcentage, mis à jour manuellement",
            measurements: "Mesures — moyenne pondérée des mesures ci-dessous",
            subGoals: "Sous-objectifs — mis à jour manuellement, en fonction des sous-objectifs ci-dessous",
            subGoalsAutomatic: "Mise à jour automatique depuis les sous-objectifs — recalculée dès qu'un sous-objectif change"
        }
    },
    GoalMeasurementRow: {
        valuePair: "{{current}} → {{target}}",
        boolean: {
            completed: "Terminé",
            incomplete: "Incomplet"
        },
        progressAriaLabel: "Progression de {{title}}",
        progressPercent: "{{value}} %"
    },
    GoalUpdates: {
        heading: "Mises à jour",
        noUpdates: "Aucune mise à jour publiée pour l'instant.",
        showAll_one: "Afficher toutes les mises à jour ({{count}})",
        showAll_other: "Afficher toutes les mises à jour ({{count}})",
        showLess: "Afficher moins"
    },
    GoalUpdateCard: {
        relativeTime: {
            today: "aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours"
        },
        overallProgress: "Progression globale",
        progressTransition: "{{previous}} % → {{current}} %",
        progressDelta: "({{delta}} %)",
        progressValue: "{{value}} %",
        measurementTransition: "{{previous}} → {{current}}",
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        }
    },
    GoalProgressUpdate: {
        addUpdate: "Mettre à jour le progrès",
        actions: {
            ariaLabel: "Actions pour la mise à jour de {{author}}",
            automaticAuthor: "cette mise à jour automatique",
            edit: "Modifier",
            delete: "Supprimer"
        },
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        addedToast: "Mise à jour de progrès ajoutée",
        addErrorToast: "Erreur lors de l’ajout de la mise à jour de progrès",
        editedToast: "Mise à jour de progrès réussie",
        editErrorToast: "Erreur lors de la mise à jour du progrès",
        deletedToast: "Mise à jour de progrès supprimée",
        deleteErrorToast: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        dialog: {
            addTitle: "Mettre à jour le progrès",
            editTitle: "Modifier le progrès",
            statusLabel: "Statut",
            progressLabel: "Progrès de l'objectif",
            progressPercent: "{{value}} %",
            progressInvalid: "Entrez un nombre entre 0 et 100.",
            progressFromMeasurements: "Calculé à partir des mesures ci-dessus. Saisissez une valeur pour le définir vous-même.",
            progressFromSubgoals: "La mise à jour automatique des sous-objectifs est activée. Le progrès ne peut pas être mis à jour manuellement.",
            loadingAriaLabel: "Chargement de l'objectif",
            measurementsLabel: "Mesures",
            measurementTarget: "Cible : {{target}}",
            measurementInvalid: "Entrez un nombre.",
            commentLabel: "Commentaire",
            commentPlaceholder: "Ajoutez un commentaire sur cette mise à jour de progrès...",
            cancel: "Annuler",
            addSubmit: "Enregistrer",
            editSubmit: "Enregistrer les modifications"
        },
        delete: {
            title: "Supprimer la mise à jour de progrès ?",
            body: "Cette mise à jour de progrès sera supprimée. Cette action ne peut pas être annulée.",
            confirm: "Supprimer",
            cancel: "Annuler"
        },
        discard: {
            title: "Abandonner cette mise à jour ?",
            body: "Vous n'avez pas encore enregistré ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            confirm: "Abandonner",
            cancel: "Continuer à modifier"
        }
    },
    GoalSubgoals: {
        deletedMember: "Membre supprimé",
        heading_one: "Sous-objectif ({{count}})",
        heading_other: "Sous-objectifs ({{count}})",
        type: {
            personal: "Objectif individuel",
            team: "Objectif d'équipe",
            organization: "Objectif d'organisation"
        },
        status: {
            onTrack: "Bien avancé",
            atRisk: "À surveiller",
            offTrack: "En difficulté",
            noStatus: "Aucun statut"
        },
        progressAriaLabel: "Progression de l'objectif {{title}}",
        progressPercent: "{{value}} %",
        dueLabel: "Échéance {{date}}",
        latestUpdate: {
            heading: "Dernière mise à jour",
            noUpdates: "Aucune mise à jour publiée.",
            today: "aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours",
            weeks_one: "il y a {{count}} semaine",
            weeks_other: "il y a {{count}} semaines"
        }
    },
    GoalNotFound: {
        heading: "Objectif introuvable",
        body: "Cet objectif n'existe pas ou vous n'y avez pas accès.",
        backToDirectory: "Retour aux employés"
    },
    ProfileFeedbackPage: {
        liveRegion: {
            loading: "Chargement du feedback",
            loadingMoreAnnouncement: "Chargement de plus de feedback",
            loadedMore_one: "{{count}} feedback supplémentaire chargé",
            loadedMore_other: "{{count}} feedbacks supplémentaires chargés"
        },
        error: "Nous n'avons pas pu charger votre feedback pour le moment.",
        accessDenied: "Vous n'avez pas accès à ce feedback.",
        ariaLabel: "Votre feedback",
        loadingMore: "Chargement en cours",
        unread: "Non lu",
        empty: {
            headline: "Vous n'avez pas encore envoyé de feedback.",
            subtext: "Le feedback que vous envoyez à votre gestionnaire — par sondage ou directement — apparaîtra ici."
        },
        unrepliedEmpty: {
            headline: "Rien en attente de réponse.",
            subtext: "Chaque message que vous avez envoyé a reçu une réponse."
        },
        status: {
            awaiting: "En attente de réponse"
        }
    },
    FeedbackToolbar: {
        filters: {
            ariaLabel: "Filtrer le feedback",
            clearAriaLabel: "Effacer le filtre de feedback",
            all: "Tout le feedback",
            unreplied: "En attente de réponse"
        },
        leaveSuggestion: "Laisser une suggestion"
    },
    FeedbackThreadDetail: {
        liveRegion: {
            loading: "Chargement de la conversation"
        },
        error: "Nous n'avons pas pu charger cette conversation pour le moment.",
        questionSenderLabel: "Workleap",
        status: {
            awaiting: "En attente de réponse"
        },
        details: {
            header: "Détails",
            sentiment: "Sentiment",
            metric: "Métrique",
            feedbackType: "Type de feedback",
            collapse: "Réduire les détails",
            expand: "Afficher les détails"
        },
        emptyReadingPane: "Sélectionnez un message à gauche pour le lire et y répondre. Les auteurs restent anonymes à moins de choisir de signer leur message."
    },
    ProfileFeedbackReplyComposer: {
        textareaLabel: "Répondre",
        placeholder: "Répondre…",
        send: "Envoyer la réponse",
        sending: "Envoi de la réponse…",
        successToast: "Réponse envoyée",
        errorToast: "Impossible d'envoyer votre réponse. Veuillez réessayer.",
        replyAnonymously: "Répondre anonymement",
        revealIdentityErrorToast: "Impossible de modifier l'anonymat pour cette conversation. Veuillez réessayer."
    },
    ProfileContactCard: {
        title: "Coordonnées"
    },
    ProfileAboutAiCard: {
        emptyRecentWork: "Aucun travail récent trouvé.",
        emptyWorkingToward: "Aucun objectif trouvé.",
        emptyRecognizedFor: "Aucune reconnaissance trouvée.",
        emptyCollaborators: "Aucun collaborateur fréquent trouvé.",
        agent: "Agent",
        lastUpdated: "Mis à jour {{timeAgo}}",
        refresh: "Régénérer maintenant",
        refreshing: "Actualisation du profil…",
        loadingLiveRegion: "Génération du profil…",
        stepWriting: "Rédaction…",
        stepLookingUp: "Recherche de {{name}}…",
        stepGoals: "Lecture des objectifs…",
        stepRecognition: "Recherche de la reconnaissance et de l'engagement…",
        stepActivity: "Survol du travail récent…",
        error: "Impossible de générer ce portrait pour le moment.",
        retry: "Réessayer",
        sectionRecentWork: "Travaux récents",
        sectionCollaborators: "Collabore avec",
        sectionWorkingToward: "Objectifs en cours",
        sectionRecognizedFor: "Reconnu pour",
        collaborationCommunication: "Communication",
        collaborationWork: "Travail",
        collaborationEngagement: "Engagement",
        collaborationCommunicationTooltip: "Ils échangent souvent par messages, courriels ou réunions.",
        collaborationWorkTooltip: "Ils collaborent sur les mêmes projets ou documents.",
        collaborationEngagementTooltip: "Liés par des objectifs et de la reconnaissance.",
        collaboratorsShowMore: "Afficher {{count}} de plus",
        collaboratorsShowLess: "Afficher moins",
        connectCta: "Connectez plus d'outils pour enrichir les aperçus du profil"
    },
    ProfileAboutCard: {
        title: "À propos",
        tenureNew: "Nouvellement arrivé",
        tenureMonths: "{{count}} mois ici",
        tenureMonths_other: "{{count}} mois ici",
        tenureYears: "{{count}} an ici",
        tenureYears_other: "{{count}} ans ici",
        interestsLabel: "Champs d'intérêt",
        addBioPrompt: "Parlez un peu de vous à vos collègues.",
        addBioCta: "Ajouter une bio"
    },
    ProfileCompetenciesCard: {
        title: "Compétences"
    },
    ProfileGoodVibesStats: {
        receivedLabel: "Bonnes Vibes reçues",
        givenLabel: "Bonnes Vibes données",
        deltaVsPriorPeriod: "{{delta}} vs période précédente",
        liveRegion: {
            loading: "Chargement des statistiques de Bonnes Vibes…"
        }
    },
    ProfileDirectReportsCard: {
        title: "Subordonnés directs"
    },
    ProfileInCommonCard: {
        title: "En commun avec vous",
        sameManager: "Même gestionnaire : {{name}}"
    },
    ProfileNotFound: {
        heading: "Profil introuvable",
        body: "Nous n'avons pas trouvé ce profil. Il a peut-être été déplacé ou supprimé.",
        backToDirectory: "Retour à l'annuaire"
    },
    ProfileTimelinePage: {
        ariaLabel: "Événements de la chronologie",
        error: "Nous n'avons pas pu charger la chronologie pour le moment.",
        liveRegion: {
            loading: "Chargement de la chronologie"
        },
        empty: {
            headline: "Aucune activité dans la chronologie de {{firstName}} pour l'instant.",
            subtext: "Le feedback, les reconnaissances et les jalons apparaissent ici au fil du temps.",
            fallbackName: "cette personne"
        },
        textValue: {
            feedback: "Feedback",
            goodVibes: "Bonnes Vibes",
            started: "Jalon",
            promotion: "Jalon",
            anniversary: "Jalon"
        }
    },
    TimelineEntryDetailRoute: {
        titleLeafByType: {
            feedback: "Feedback",
            goodVibes: "Bonne Vibe",
            started: "Jalon",
            promotion: "Jalon",
            anniversary: "Jalon"
        }
    },
    TimelineFilterBar: {
        typeFilter: {
            ariaLabel: "Filtrer par type",
            clearAriaLabel: "Effacer le filtre de type",
            options: {
                all: "Tous les types",
                milestones: "Jalons",
                goodVibes: "Bonnes Vibes",
                feedback: "Feedback"
            }
        },
        timeFilter: {
            ariaLabel: "Filtrer par période",
            clearAriaLabel: "Effacer le filtre de période",
            options: {
                allTime: "Toutes les périodes",
                last90Days: "90 derniers jours",
                thisYear: "Cette année"
            }
        },
        resetFilters: "Effacer les filtres",
        eventCount_one: "{{count}} événement",
        eventCount_other: "{{count}} événements"
    },
    TimelineFeedbackCard: {
        from: "De {{name}}",
        deletedMember: "Membre supprimé",
        openDetailAriaLabel: "Ouvrir le détail du feedback, {{date}}"
    },
    TimelineSentimentTag: {
        positive: "Positif",
        constructive: "Constructif",
        neutral: "Neutre"
    },
    TimelineGoodVibesCard: {
        eyebrow: "Bonnes Vibes",
        unknownSender: "Membre supprimé",
        openDetailAriaLabel: "Ouvrir la Bonne Vibe de {{sender}}, {{date}}",
        from: "De {{name}}",
        reactionTotal_one: "{{count}} réaction",
        reactionTotal_other: "{{count}} réactions"
    },
    TimelineStartedCard: {
        eyebrow: "Arrivée",
        narrative: "S'est joint à {{orgName}}.",
        narrativeWithTitle: "S'est joint à {{orgName}} à titre de <strong>{{jobTitle}}</strong>.",
        openDetailAriaLabel: "Ouvrir le détail du jalon d'arrivée, {{date}}"
    },
    TimelinePromotionCard: {
        eyebrow: "Changement de poste",
        narrative: "<lead>Titre modifié pour </lead><strong>{{jobTitle}}</strong><tail> (auparavant {{previousJobTitle}}).</tail>",
        openDetailAriaLabel: "Ouvrir le détail du jalon de changement de poste, {{date}}"
    },
    TimelineAnniversaryCard: {
        eyebrow: "Anniversaire",
        narrative_one: "{{firstName}} travaille chez {{orgName}} depuis <strong>{{count}} an</strong>.",
        narrative_other: "{{firstName}} travaille chez {{orgName}} depuis <strong>{{count}} ans</strong>.",
        openDetailAriaLabel: "Ouvrir le détail du jalon d'anniversaire, {{date}}"
    },
    GoodVibesDetailDialog: { ...goodVibeDetailDialogFr },
    FeedbackDetailDialog: {
        heading: "Feedback de {{sender}}",
        unknownSender: "Quelqu'un",
        deletedMember: "Membre supprimé",
        from: "De {{name}}"
    },
    MilestoneDetailDialog: {
        heading: "Détail du jalon",
        started: {
            eyebrow: "Arrivée",
            narrative: "S'est joint à {{orgName}}.",
            narrativeWithTitle: "S'est joint à {{orgName}} à titre de <strong>{{jobTitle}}</strong>."
        },
        promotion: {
            eyebrow: "Changement de poste",
            narrative: "<lead>Titre modifié pour </lead><strong>{{jobTitle}}</strong><tail> (auparavant {{previousJobTitle}}).</tail>"
        },
        anniversary: {
            eyebrow: "Anniversaire",
            narrative_one: "{{firstName}} travaille chez {{orgName}} depuis <strong>{{count}} an</strong>.",
            narrative_other: "{{firstName}} travaille chez {{orgName}} depuis <strong>{{count}} ans</strong>."
        }
    },
    ProfileGoodVibesPage: {
        error: "Nous n'avons pas pu charger les Bonnes Vibes pour le moment.",
        empty: "Aucune reconnaissance ne correspond à ces filtres.",
        resetFilters: "Effacer les filtres",
        sendInClassic: "Envoyer des Bonnes Vibes",
        sendGoodVibes: "Envoyer des Good Vibes",
        giveModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "Cette fonctionnalité n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour la compléter. Rien ne sera perdu.",
            cancel: "Annuler",
            continue: "Continuer dans Officevibe"
        },
        composeDialog: {
            title: "Envoyer des Bonnes Vibes",
            recipientsLabel: "À",
            recipientsPlaceholder: "Ajouter quelqu'un",
            recipientsSearchPlaceholder: "Rechercher des employés",
            recipientsSelectedLabel: "Destinataires sélectionnés",
            recipientsEmpty: "Aucun résultat",
            recipientsLoading: "Recherche en cours…",
            subjectLabel: "Sujet",
            subjectPlaceholder: "Qu'ont-ils fait ?",
            writeOwnLabel: "Écrire le vôtre, ou choisir un sujet",
            useTypedLabel: 'Utiliser "{{text}}"',
            customCategoryLabel: "Personnalisé",
            changeSubjectLabel: "Changer",
            recipientsRemoveLabel: "Retirer {{name}}",
            messageLabel: "Message",
            messagePlaceholder: "Dites ce qu'ils ont fait et pourquoi c'était important.",
            messageCharactersLeft: "{{count}} caractères restants",
            audienceLabel: "Audience",
            directLabel: "Direct",
            directDescription: "Seulement les destinataires, leurs gestionnaires et leurs supérieurs peuvent voir ceci.",
            publicLabel: "Public",
            publicDescription: "Tous les membres de l'entreprise peuvent voir ceci.",
            fromLabel: "De {{name}}",
            cancelLabel: "Annuler",
            submitDirectLabel: "Envoyer directement",
            submitPublicLabel: "Envoyer publiquement",
            discardChangesTitle: "Abandonner cette Bonne Vibe ?",
            discardChangesBody: "Vous n'avez pas encore envoyé ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            discardChangesConfirmLabel: "Abandonner",
            discardChangesCancelLabel: "Continuer à modifier",
            successMessage: "Vous venez d'ensoleiller la journée de quelqu'un !",
            successCloseLabel: "Fermer",
            mostUsedThisMonthLabel: "Les plus utilisées ce mois-ci",
            usageCountLabel: "{{count}} utilisations ce mois-ci"
        },
        giveError: "Impossible d'envoyer. Veuillez réessayer.",
        visibility: {
            public: "Publique",
            direct: "Directe"
        },
        sealed: {
            ariaLabel: "Nouvelle Bonne Vibe pour {{recipients}}. Appuyez et maintenez pour l'ouvrir.",
            badge: "Nouvelle Bonne Vibe",
            pressAndHold: "Appuyez et maintenez pour l'ouvrir"
        },
        emptyTimeframe: {
            "7d": "Aucune Bonne Vibe au cours des 7 derniers jours.",
            "30d": "Aucune Bonne Vibe au cours des 30 derniers jours.",
            "3m": "Aucune Bonne Vibe au cours des 3 derniers mois.",
            "6m": "Aucune Bonne Vibe au cours des 6 derniers mois.",
            quarter: "Aucune Bonne Vibe ce trimestre.",
            year: "Aucune Bonne Vibe cette année."
        },
        emptyAllTime: "Aucune Bonne Vibe pour le moment.",
        listAriaLabel: "Bonnes Vibes",
        liveRegion: {
            loading: "Chargement des Bonnes Vibes…",
            loadingMore: "Chargement d'autres Bonnes Vibes…",
            loadedMore_one: "{{count}} Bonne Vibe supplémentaire chargée",
            loadedMore_other: "{{count}} Bonnes Vibes supplémentaires chargées"
        },
        filters: {
            audience: {
                label: "Audience",
                triggerPrefix: "Audience :",
                clearAriaLabel: "Effacer le filtre d'audience",
                options: {
                    all: "Tous",
                    public: "Public",
                    direct: "Direct"
                }
            },
            direction: {
                groupLabel: "Direction",
                triggerPrefix: "Direction :",
                clearAriaLabel: "Effacer le filtre de direction",
                options: {
                    all: "Tous",
                    received: "Reçus",
                    given: "Envoyés"
                }
            },
            themes: {
                label: "Thèmes",
                ariaLabel: "Filtrer par thème",
                clearFilter: "Effacer le filtre"
            },
            timeframe: {
                ariaLabel: "Filtrer par période",
                clearAriaLabel: "Effacer le filtre de période",
                options: {
                    "7d": "7 derniers jours",
                    "30d": "30 derniers jours",
                    "3m": "3 derniers mois",
                    "6m": "6 derniers mois",
                    quarter: "Ce trimestre",
                    year: "Cette année",
                    all: "Depuis toujours"
                }
            }
        }
    },
    OrganizationGoalsPage: {
        create: {
            newGoal: "Nouvel objectif",
            error: "Erreur lors de la création de l’objectif"
        },
        error: "Impossible de charger les objectifs de l'organisation pour l'instant.",
        empty: {
            heading: "Aucun objectif pour l'instant",
            hint: "Les objectifs de l'organisation apparaîtront ici dès qu'un objectif sera créé."
        },
        noResults: "Aucun objectif trouvé",
        noResultsHint: "Essayez d'ajuster les filtres.",
        totalCount_one: "{{count}} objectif",
        totalCount_other: "{{count}} objectifs",
        tableAriaLabel: "Objectifs de l'organisation",
        columns: {
            title: "Objectif",
            dueDate: "Échéance",
            status: "Statut",
            progress: "Progression",
            lastUpdatedAt: "Mis à jour"
        },
        liveRegion: {
            loading: "Chargement des objectifs",
            loadingMoreAnnouncement: "Chargement d'autres objectifs",
            loadedMore_one: "{{count}} objectif supplémentaire chargé",
            loadedMore_other: "{{count}} objectifs supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats",
            sortChanged: "Trié par {{column}} {{direction}}",
            ascending: "ordre croissant",
            descending: "ordre décroissant"
        }
    },
    OrganizationGoodVibesPage: {
        error: "Nous n'avons pas pu charger les Bonnes Vibes pour le moment.",
        empty: "Aucune Bonne Vibe pour le moment.",
        emptyFiltered: "Aucune Bonne Vibe ne correspond à ces filtres.",
        sendInClassic: "Envoyer des Bonnes Vibes",
        sendGoodVibes: "Envoyer des Good Vibes",
        giveModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "Cette fonctionnalité n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour la compléter. Rien ne sera perdu.",
            cancel: "Annuler",
            continue: "Continuer dans Officevibe"
        },
        composeDialog: {
            title: "Envoyer des Bonnes Vibes",
            recipientsLabel: "À",
            recipientsPlaceholder: "Ajouter quelqu'un",
            recipientsSearchPlaceholder: "Rechercher des employés",
            recipientsSelectedLabel: "Destinataires sélectionnés",
            recipientsEmpty: "Aucun résultat",
            recipientsLoading: "Recherche en cours…",
            subjectLabel: "Sujet",
            subjectPlaceholder: "Qu'ont-ils fait ?",
            writeOwnLabel: "Écrire le vôtre, ou choisir un sujet",
            useTypedLabel: 'Utiliser "{{text}}"',
            customCategoryLabel: "Personnalisé",
            changeSubjectLabel: "Changer",
            recipientsRemoveLabel: "Retirer {{name}}",
            messageLabel: "Message",
            messagePlaceholder: "Dites ce qu'ils ont fait et pourquoi c'était important.",
            messageCharactersLeft: "{{count}} caractères restants",
            audienceLabel: "Audience",
            directLabel: "Direct",
            directDescription: "Seulement les destinataires, leurs gestionnaires et leurs supérieurs peuvent voir ceci.",
            publicLabel: "Public",
            publicDescription: "Tous les membres de l'entreprise peuvent voir ceci.",
            fromLabel: "De {{name}}",
            cancelLabel: "Annuler",
            submitDirectLabel: "Envoyer directement",
            submitPublicLabel: "Envoyer publiquement",
            discardChangesTitle: "Abandonner cette Bonne Vibe ?",
            discardChangesBody: "Vous n'avez pas encore envoyé ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            discardChangesConfirmLabel: "Abandonner",
            discardChangesCancelLabel: "Continuer à modifier",
            successMessage: "Vous venez d'ensoleiller la journée de quelqu'un !",
            successCloseLabel: "Fermer",
            mostUsedThisMonthLabel: "Les plus utilisées ce mois-ci",
            usageCountLabel: "{{count}} utilisations ce mois-ci"
        },
        giveError: "Impossible d'envoyer. Veuillez réessayer.",
        sealed: {
            ariaLabel: "Nouvelle Bonne Vibe pour {{recipients}}. Appuyez et maintenez pour l'ouvrir.",
            badge: "Nouvelle Bonne Vibe",
            pressAndHold: "Appuyez et maintenez pour l'ouvrir"
        },
        listAriaLabel: "Bonnes Vibes",
        liveRegion: {
            loading: "Chargement des Bonnes Vibes",
            loadingMore: "Chargement d'autres Bonnes Vibes",
            loadedMore_one: "{{count}} Bonne Vibe supplémentaire chargée",
            loadedMore_other: "{{count}} Bonnes Vibes supplémentaires chargées",
            results_one: "{{count}} Bonne Vibe",
            results_other: "{{count}} Bonnes Vibes"
        },
        filters: {
            groupLabel: "Filtrer les Bonnes Vibes",
            collection: {
                ariaLabel: "Collection",
                triggerPrefix: "Collection :",
                clearAriaLabel: "Effacer le filtre de collection",
                all: "Toutes",
                unavailable: "Collection non disponible"
            }
        }
    },
    GoodVibeCard: { ...goodVibeCardFr },
    ProfileEngagementPage: {
        liveRegion: {
            loading: "Chargement de l'engagement"
        }
    },
    ProfileEngagementMetricsPage: {
        breadcrumb: {
            ariaLabel: "Mesures",
            here: "Mesures"
        },
        pageTitle: "Votre engagement",
        pageDescription: "Consultez votre score d'engagement et vos résultats par mesure. Vos réponses sont anonymes et vous seul y avez accès.",
        privacyLabel: "Qui peut voir ceci",
        privacyTooltip: "Vous seul pouvez voir vos scores d'engagement.",
        metricTooltipLabel: "À propos de {{metric}}",
        metricTooltips: {},
        subMetrics: {
            eyebrow: "Sous-mesures",
            helper: "Sélectionnez-en une pour voir sa propre tendance.",
            empty: "Aucun détail de sous-mesure pour le moment."
        },
        chart: {
            title: "Votre {{metric}} vs {{company}}",
            titleWithSub: "Votre {{sub}} vs {{company}}",
            ariaLabel: "Score de {{subject}} au cours des 3 derniers mois",
            legendYou: "Vous",
            legendCompany: "Entreprise",
            scoreOutOf: "/ 10",
            changePeriod: "depuis 3 mois",
            changeNone: "Aucun changement depuis 3 mois",
            changeUnavailable: "Aucune comparaison disponible",
            subMetricEmpty: "Pas encore de données de tendance pour cette sous-mesure.",
            subMetricNoHistory: "Pas d'historique pour {{sub}} — {{metric}} est affiché pour référence."
        },
        error: {
            title: "Impossible de charger vos mesures d'engagement pour le moment."
        },
        empty: {
            description: "Vos scores d'engagement s'afficheront ici lorsque vous aurez répondu à quelques sondages."
        },
        liveRegion: {
            loading: "Chargement de vos mesures d'engagement"
        }
    },
    ProfileGoodVibesPanel: {
        title: "Vos Bonnes Vibes",
        sendGoodVibes: "Envoyer des Good Vibes",
        sendInClassic: "Envoyer des Bonnes Vibes",
        toggle: {
            ariaLabel: "Direction des Bonnes Vibes",
            received: "Reçus",
            sent: "Envoyés"
        },
        emptyReceived: "Aucune Bonne Vibe pour le moment — la reconnaissance que vous recevez apparaîtra ici.",
        emptySent: "Vous n'avez pas encore envoyé de Bonnes Vibes.",
        error: "Nous n'avons pas pu charger les Bonnes Vibes pour le moment.",
        listAriaLabel: "Bonnes Vibes",
        giveError: "Impossible d'envoyer. Veuillez réessayer.",
        visibility: {
            public: "Publique",
            direct: "Directe"
        },
        sealed: {
            ariaLabel: "Nouvelle Bonne Vibe pour {{recipients}}. Appuyez et maintenez pour l'ouvrir.",
            badge: "Nouvelle Bonne Vibe",
            pressAndHold: "Appuyez et maintenez pour l'ouvrir"
        },
        liveRegion: {
            loading: "Chargement des Bonnes Vibes…",
            loadingMore: "Chargement d'autres Bonnes Vibes…",
            loadedMore_one: "{{count}} Bonne Vibe supplémentaire chargée",
            loadedMore_other: "{{count}} Bonnes Vibes supplémentaires chargées"
        },
        composeDialog: {
            title: "Envoyer des Bonnes Vibes",
            recipientsLabel: "À",
            recipientsPlaceholder: "Ajouter quelqu'un",
            recipientsSearchPlaceholder: "Rechercher des employés",
            recipientsSelectedLabel: "Destinataires sélectionnés",
            recipientsEmpty: "Aucun résultat",
            recipientsLoading: "Recherche en cours…",
            subjectLabel: "Sujet",
            subjectPlaceholder: "Qu'ont-ils fait ?",
            writeOwnLabel: "Écrire le vôtre, ou choisir un sujet",
            useTypedLabel: 'Utiliser "{{text}}"',
            customCategoryLabel: "Personnalisé",
            changeSubjectLabel: "Changer",
            recipientsRemoveLabel: "Retirer {{name}}",
            messageLabel: "Message",
            messagePlaceholder: "Dites ce qu'ils ont fait et pourquoi c'était important.",
            messageCharactersLeft: "{{count}} caractères restants",
            audienceLabel: "Audience",
            directLabel: "Direct",
            directDescription: "Seulement les destinataires, leurs gestionnaires et leurs supérieurs peuvent voir ceci.",
            publicLabel: "Public",
            publicDescription: "Tous les membres de l'entreprise peuvent voir ceci.",
            fromLabel: "De {{name}}",
            cancelLabel: "Annuler",
            submitDirectLabel: "Envoyer directement",
            submitPublicLabel: "Envoyer publiquement",
            discardChangesTitle: "Abandonner cette Bonne Vibe ?",
            discardChangesBody: "Vous n'avez pas encore envoyé ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            discardChangesConfirmLabel: "Abandonner",
            discardChangesCancelLabel: "Continuer à modifier",
            successMessage: "Vous venez d'ensoleiller la journée de quelqu'un !",
            successCloseLabel: "Fermer",
            mostUsedThisMonthLabel: "Les plus utilisées ce mois-ci",
            usageCountLabel: "{{count}} utilisations ce mois-ci"
        },
        giveModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "Cette fonctionnalité n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour la compléter. Rien ne sera perdu.",
            cancel: "Annuler",
            continue: "Continuer dans Officevibe"
        }
    },
    ProfileProgressionPage: {
        latestReview: {
            title: "Dernière évaluation",
            viewAll: "Voir toutes les évaluations",
            ratingAriaLabel: "Note {{score}} sur {{scale}}",
            deliveryBadge: {
                shared: "Partagée",
                acknowledged: "Confirmée"
            },
            attribution: {
                shared: "{{date}}",
                acknowledged: "{{date}}"
            },
            openAriaLabel: "Ouvrir l'évaluation : {{cycleTitle}}",
            empty: "Aucune évaluation ne vous a encore été partagée.",
            error: "Nous n'avons pas pu charger votre dernière évaluation.",
            liveRegion: {
                loading: "Chargement de votre dernière évaluation"
            }
        },
        experience: {
            title: "Expérience",
            viewTimeline: "Voir la chronologie",
            sinceDate: "depuis {{date}}",
            dateRange: "{{start}} – {{end}}",
            promotionWithDate: "Promotion · {{date}}",
            anniversary_one: "On célèbre {{count}} an chez {{orgName}}",
            anniversary_other: "On célèbre {{count}} ans chez {{orgName}}",
            empty: "Aucune expérience à afficher pour le moment.",
            error: "Nous n'avons pas pu charger votre expérience pour le moment.",
            liveRegion: {
                loading: "Chargement de votre expérience"
            }
        },
        goals: {
            title: "Objectifs",
            viewAll: "Voir tous mes objectifs",
            statusAndProgress: "{{status}} · {{progress}} %",
            status: {
                onTrack: "Sur la bonne voie",
                atRisk: "À risque",
                offTrack: "Hors piste",
                noStatus: "Aucun statut"
            },
            untitled: "Objectif sans titre",
            draft: "Brouillon",
            finish: "Terminer",
            empty: "Aucun objectif pour le moment.",
            error: "Nous n'avons pas pu charger vos objectifs pour le moment.",
            liveRegion: {
                loading: "Chargement de vos principaux objectifs"
            }
        },
        competencies: {
            title: "Compétences",
            empty: "Aucune compétence ajoutée pour l'instant.",
            editAction: "Modifier les compétences",
            error: "Nous n'avons pas pu charger les compétences pour le moment."
        },
        liveRegion: {
            loading: "Chargement de la progression"
        }
    },
    EngagementScoresPanel: {
        title: "Votre engagement",
        privacyLabel: "Qui peut voir ceci",
        privacyTooltip: "Vous seul pouvez voir vos scores d'engagement.",
        takeSurvey: "Répondre au sondage",
        opensInNewTab: "ouvre dans un nouvel onglet",
        scoreAriaLabel: "Score d'engagement de {{score}} sur {{max}}, {{band}}",
        bandAriaLabel: "Palier du score : {{band}}",
        changeValue: "{{value}} pts depuis 3 mois",
        changeNone: "Aucun changement depuis 3 mois",
        directionUp: "En hausse",
        directionDown: "En baisse",
        directionNone: "Aucun changement",
        delta: "{{value}} pts",
        deltaUnavailable: "Aucune comparaison disponible",
        legendYou: "Vous",
        legendCompany: "Entreprise",
        chartAriaLabel: "Votre score d'engagement des 3 derniers mois, comparé à la moyenne de l'entreprise.",
        chartAriaLabelSolo: "Votre score d'engagement des 3 derniers mois.",
        topGains: "Plus fortes hausses",
        topDecreases: "Plus fortes baisses",
        showAllMetrics: "Afficher toutes les mesures",
        hideAllMetrics: "Masquer toutes les mesures",
        allMetricsAriaLabel: "Toutes les mesures d'engagement",
        openMetricDetailAriaLabel: "Ouvrir les détails de {{label}}",
        empty: "Vos scores d'engagement s'afficheront ici lorsque vous aurez répondu à quelques sondages.",
        error: "Impossible de charger vos scores d'engagement pour le moment.",
        loadingAnnouncement: "Chargement de vos scores d'engagement"
    },
    EngagementFeedbackPanel: {
        title: "Votre feedback",
        viewAll: "Voir tout",
        sendFeedback: "Envoyer du feedback",
        empty: "Aucun feedback pour le moment.",
        error: "Nous n'avons pas pu charger votre feedback.",
        sender: {
            you: "Vous",
            anonymousYou: "Anonyme (vous)"
        },
        status: {
            awaiting: "En attente d'une réponse",
            replied: "Répondu"
        },
        date: {
            week: "Semaine du {{date}}",
            absolute: "{{date}}"
        },
        openRow: "Ouvrir le feedback de {{sender}}, {{status}}. {{snippet}}",
        openRowUnread: "Ouvrir le feedback de {{sender}}, {{status}}, nouvelle réponse. {{snippet}}",
        liveRegion: {
            loading: "Chargement de votre feedback"
        }
    },
    EngagementFeedbackThreadDialog: {
        title: "Feedback",
        close: "Fermer"
    },
    EngagementRating: {
        critical: "Critique",
        veryLow: "Très faible",
        low: "Faible",
        good: "Bon",
        veryGood: "Très bon",
        great: "Excellent",
        amazing: "Exceptionnel"
    },
    OrganizationCompanyAboutWidget: {
        label: "À propos",
        editButton: "Modifier",
        missionLabel: "Mission",
        valuesLabel: "Valeurs",
        liveRegion: {
            loading: "Chargement du profil de l'entreprise"
        },
        adminEmpty: {
            missionPlaceholder: "Ajoutez la mission de votre entreprise pour aider vos employés à comprendre ce que vous bâtissez.",
            valuesPlaceholder: "Ajoutez les valeurs de votre entreprise pour aider vos employés à savoir ce qui compte."
        }
    },
    OrganizationEngagementWidget: {
        title: "Engagement de l'organisation",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir tout l'engagement",
        score: "Score",
        enps: "eNPS",
        band: {
            "needs-work": "À améliorer",
            neutral: "Neutre",
            good: "Bon",
            excellent: "Excellent"
        },
        delta_one: "{{sign}}{{value}} pt",
        delta_other: "{{sign}}{{value}} pts",
        footer_one: "30 derniers jours · Basé sur {{rate}} % de participation · {{count}} sur {{invited}} a répondu",
        footer_other: "30 derniers jours · Basé sur {{rate}} % de participation · {{count}} sur {{invited}} ont répondu",
        scoreAriaLabel: "Score {{value}} sur 10",
        enpsAriaLabel: "eNPS {{value}} sur une échelle de -100 à +100",
        noData: "Aucune donnée"
    },
    OrganizationOverviewPage: {
        liveRegion: {
            loading: "Chargement de l'aperçu de l'organisation"
        },
        widgetError: {
            engagement: "Impossible de charger l'engagement.",
            goals: "Impossible de charger les objectifs de l'organisation."
        }
    },
    OrganizationGoalsWidget: {
        title: "Objectifs de l'organisation",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir tous les objectifs de l'organisation",
        untitled: "Sans titre",
        unassignedOwner: "Non assigné",
        progressAriaLabel: "{{title}} : {{progress}} % complété",
        percent: "{{progress}} %",
        status: {
            onTrack: "Sur la bonne voie",
            atRisk: "À risque",
            offTrack: "Hors piste",
            noStatus: "Sans statut"
        },
        empty: {
            title: "Aucun objectif publié pour l'organisation."
        }
    },
    OrganizationCompanyOrgChartWidget: {
        label: "Organigramme",
        viewInOrgChart: "Voir l'organigramme",
        treeLabel: "Organigramme de l'entreprise",
        unknownMember: "Membre inconnu",
        youBadge: "Vous",
        youSuffix: "{{name}} (vous)",
        viewMore: "Voir {{count}} de plus dans l'organigramme",
        tooltipMore: "et {{count}} de plus",
        liveRegion: {
            loading: "Chargement de l'organigramme"
        },
        empty: {
            title: "Personne ne figure encore dans l'organigramme."
        },
        error: {
            title: "Impossible de charger l'organigramme.",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        },
        orgChartModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "L'organigramme complet n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Pingboard pour le consulter.",
            cancel: "Annuler",
            continue: "Continuer dans Pingboard"
        }
    },
    OrganizationFeedbackPage: {
        error: "Impossible de charger le feedback de l'organisation pour l'instant.",
        empty: {
            headline: "Aucun feedback en attente.",
            subtext: "Les nouveaux messages de toute votre organisation apparaîtront ici."
        },
        filteredEmpty: {
            headline: "Aucun feedback ne correspond à ces filtres",
            subtext: "Essayez d'effacer vos filtres ou votre recherche."
        },
        liveRegion: {
            loading: "Chargement du feedback de l'organisation",
            loadingMoreAnnouncement: "Chargement de plus de feedback",
            loadedMore_one: "{{count}} feedback supplémentaire chargé",
            loadedMore_other: "{{count}} feedbacks supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        },
        ariaLabel: "Feedback de l'organisation",
        loadingMore: "Chargement en cours",
        unread: "Non lu",
        moreCollapsed_one: "+{{count}} autre",
        moreCollapsed_other: "+{{count}} autres",
        toMonitor: "À surveiller",
        toMonitorTooltip: "Feedback à surveiller signalé par {{category}}",
        status: {
            awaiting: "En attente de réponse"
        },
        totalCount_one: "{{count}} feedback",
        totalCount_other: "{{count}} feedbacks",
        sender: {
            anonymous: "Anonyme"
        },
        typeFilter: {
            label: "Type de feedback",
            menuAriaLabel: "Filtrer par type de feedback",
            clearFilter: "Effacer le filtre de type de feedback",
            dismissAriaLabel: "Retirer le filtre de type de feedback",
            triggerLabel: "Type de feedback · {{count}}",
            triggerLabelSingle: "Type de feedback : {{label}}"
        }
    },
    OrganizationFeedbackToolbar: {
        searchPlaceholder: "Rechercher du feedback",
        searchAriaLabel: "Rechercher du feedback",
        filters: {
            unreplied: "Tous les sans réponse",
            unread: "Tous les non lus",
            all: "Tous les feedbacks",
            ariaLabel: "Filtrer les feedbacks",
            clearAriaLabel: "Effacer le filtre de feedback"
        },
        timeFrame: {
            ariaLabel: "Période",
            clearAriaLabel: "Effacer le filtre de période",
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année"
        },
        addFilter: {
            label: "Ajouter un filtre",
            menuAriaLabel: "Afficher ou masquer les filtres"
        },
        resetFilters: "Effacer les filtres",
        fullscreen: {
            expand: "Agrandir en plein écran",
            collapse: "Fermer le plein écran"
        }
    },
    SummarizeFeedbackButton: {
        button: "Résumer tout le feedback",
        pending: "Collecte du feedback…",
        error: "Impossible de résumer le feedback. Veuillez réessayer.",
        prompt: "Résumer le feedback de mon organisation"
    },
    OrganizationFeedbackDetail: {
        error: "Impossible de charger ce feedback pour l'instant.",
        liveRegion: {
            loading: "Chargement du feedback"
        },
        toMonitor: "À surveiller",
        toMonitorTooltip: "Feedback à surveiller signalé par {{category}}",
        toMonitorBanner: {
            label: "Signalé à surveiller",
            hide: "Masquer",
            why: "Pourquoi ?",
            accuracyPrompt: "Ce signalement était-il exact ?",
            accurate: "Indiquer que ce signalement est exact",
            inaccurate: "Indiquer que ce signalement est inexact",
            thanks: "Merci pour votre commentaire.",
            reasonDialog: {
                title: "Qu'avons-nous mal évalué ?",
                description: "Votre commentaire aide à ajuster les critères de signalement à surveiller. Facultatif.",
                placeholder: "Ce signalement était erroné parce que...",
                skip: "Ignorer",
                submit: "Envoyer le commentaire"
            }
        },
        removeToMonitorFlag: {
            button: "Retirer l'indicateur à surveiller",
            dialog: {
                title: "Retirer l'indicateur ?",
                description: "L'indicateur à surveiller sera retiré de ce feedback. Cette action est irréversible.",
                cancel: "Annuler",
                confirm: "Retirer l'indicateur"
            },
            success: "Indicateur à surveiller retiré.",
            error: "Impossible de retirer l'indicateur à surveiller. Veuillez réessayer."
        },
        status: {
            awaiting: "En attente de réponse"
        },
        sender: {
            anonymous: "Anonyme"
        },
        closestReportingManager: {
            from: "de l'équipe de {{name}}",
            unavailable: "(Gestionnaire non disponible)"
        },
        questionSenderLabel: "Workleap",
        details: {
            header: "Détails",
            sentiment: "Sentiment",
            metric: "Métrique",
            status: "Statut",
            feedbackType: "Type de feedback",
            topics: "Sujets",
            collapse: "Réduire les détails",
            expand: "Afficher les détails"
        },
        emptyReadingPane: "Sélectionnez un message à gauche pour le lire et y répondre. Les auteurs restent anonymes à moins de choisir de signer leur message."
    },
    FeedbackReplyComposer: {
        textareaLabel: "Répondre au feedback",
        placeholder: "Répondre…",
        send: "Envoyer la réponse",
        sending: "Envoi de la réponse…",
        successToast: "Réponse envoyée",
        errorToast: "Impossible d'envoyer votre réponse. Veuillez réessayer.",
        helpMeReply: "Aidez-moi à répondre",
        undo: "Annuler",
        generating: "Lecture de la conversation…",
        generatingLiveRegion: "Génération d'une réponse",
        generateErrorToast: "Une suggestion de réponse n'a pas pu être générée. Veuillez réessayer.",
        improve: "Améliorer",
        improveLessFormal: "Moins formel",
        improveMoreConcise: "Plus concis",
        improveMoreNeutral: "Plus neutre",
        improveMoreSimple: "Plus simple",
        improveMoreActionable: "Plus concret",
        improveMoreAppreciative: "Plus reconnaissant",
        improveStartOver: "Recommencer"
    },
    OrgChartPage: {
        title: "Organigramme",
        liveRegion: {
            loading: "Chargement…"
        }
    },
    OrgChartNodeList: {
        unknownMember: "Membre inconnu",
        openRole: "Poste à pourvoir"
    }
};
